import { useState, ReactElement } from "react";
import {
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  Typography,
} from "@mui/material";
import StandardDialog from "../../../components/StandardDialog";

interface CreateRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (roleName: string, accessType: "full" | "none") => Promise<void>;
  creating: boolean;
}

function CreateRoleDialog({
  open,
  onClose,
  onCreate,
  creating,
}: CreateRoleDialogProps): ReactElement {
  const [roleName, setRoleName] = useState<string>("");
  const [accessType, setAccessType] = useState<"full" | "none">("none");
  const [error, setError] = useState<string>("");

  const handleCreate = async (): Promise<void> => {
    // Validate role name
    if (!roleName.trim()) {
      setError("Role name is required");
      return;
    }

    if (roleName.trim().length < 2) {
      setError("Role name must be at least 2 characters");
      return;
    }

    if (roleName.trim().length > 50) {
      setError("Role name must be less than 50 characters");
      return;
    }

    setError("");
    await onCreate(roleName.trim(), accessType);
    
    // Reset form on success
    if (!creating) {
      setRoleName("");
      setAccessType("none");
    }
  };

  const handleClose = (): void => {
    if (!creating) {
      setRoleName("");
      setAccessType("none");
      setError("");
      onClose();
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Create Role"
      confirmText="Create"
      onConfirm={handleCreate}
      confirmDisabled={creating || !roleName.trim()}
      disableClose={creating}
      maxWidth="sm"
    >
      <Box>
        <TextField
          fullWidth
          label="Role Name"
          value={roleName}
          onChange={(e) => {
            setRoleName(e.target.value);
            setError("");
          }}
          error={!!error}
          helperText={error || "Enter a name for the new role"}
          disabled={creating}
          autoFocus
          sx={{ mb: 3 }}
        />

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              Access
            </Typography>
          </FormLabel>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
            This allows bulk select all access for easy change. Full Access grants all permissions, No Access grants no permissions.
          </Typography>
          <RadioGroup
            value={accessType}
            onChange={(e) => setAccessType(e.target.value as "full" | "none")}
          >
            <FormControlLabel
              value="full"
              control={<Radio disabled={creating} />}
              disabled={creating}
              label="Full Access"
            />
            <FormControlLabel
              value="none"
              control={<Radio disabled={creating} />}
              disabled={creating}
              label="No Access"
            />
          </RadioGroup>
        </FormControl>
      </Box>
    </StandardDialog>
  );
}

export default CreateRoleDialog;
